import { NavigationCollectionType, TextSearchQueryWhitespaceMode, type IReaderDocument, type IReaderDocumentSearch, type IReaderViewSearchAgent, type ISearchResultItem } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type { ICirrusReaderPublicationSearchResultItem } from "../shared/CirrusReaderModel";

let _documentSearch: IReaderDocumentSearch;
let _searchAgent: IReaderViewSearchAgent;

export const useCirrusSearch = () => {

    const { $cirrusReaderPublication, $cirrusReaderView, $cirrusReaderReadingSystem } = useNuxtApp();

    const ensureInitialised = () => {
        if (_documentSearch && _searchAgent) return;
        const searchAnnotationLayer = useAnnotationLayer('vanilla-reader__publication-search-results', {
            key: 'publication-search',
            destroyOnLastUnmount: false,
            layerOptions: { layerStyle: { 'mix-blend-mode': 'multiply' } },
            defaultAnnotationOptions: { rangeStyle: { backgroundColor: 'violet' } },
        });
        _documentSearch = $cirrusReaderReadingSystem.colibrioReadingSystem.getReaderDocumentSearch();
        _searchAgent = _documentSearch.createReaderViewSearchAgent(searchAnnotationLayer.getColibrioLayer());
    }

    const searchPublication = (
        searchString: string,
        resultSetMaxSize: number = 50,
        documentIndexes?: number[],
        highlightResults: boolean = true,
    ): Promise<ICirrusReaderPublicationSearchResultItem[]> => {
        if (!$cirrusReaderPublication.colibrioReaderPublication) {
            return Promise.resolve([]);
        }
        ensureInitialised();
        return _search(searchString, resultSetMaxSize, 50, documentIndexes, highlightResults);
    }

    const _search = async (searchText: string,
                  resultLimit: number,
                  padding: number = 50,
                  documentIndexes: number[] | undefined = undefined,
                  highlightResults: boolean
    ): Promise<ICirrusReaderPublicationSearchResultItem[]> => {

        let documentsToSearch: IReaderDocument[];

        const textQuery = _documentSearch.createTextQuery(searchText, {
            ignoreCase: true,
            whitespaceMode: TextSearchQueryWhitespaceMode.IGNORE,
            searchResultOptions: {
                includeContentBlocks: true,
                numberOfCharactersBefore: padding,
                numberOfCharactersAfter: padding,
            }
        });

        if(highlightResults) {
            _searchAgent?.setSearchQuery(textQuery);
        }

        if(documentIndexes) {
            documentsToSearch = _getColibrioReaderDocumentsFromSpineIndexes(documentIndexes);
        } else {
            documentsToSearch = $cirrusReaderView.colibrioReaderView.getReaderDocuments();
        }

        const resultIterator = textQuery.execute(documentsToSearch);
        const colibrioResultSet = await resultIterator.take(resultLimit);

        return _createResultItemArray(colibrioResultSet);
    }

    const _getColibrioReaderDocumentsFromSpineIndexes = (_documentIndexes: number[]): IReaderDocument[] => {
        return $cirrusReaderPublication.colibrioReaderPublication.getSpine().filter((_doc, index)=>{
            return _documentIndexes.includes(index);
        });
    }

    const _createResultItemArray = async (colibrioSearchResultItems: ISearchResultItem[]): Promise<ICirrusReaderPublicationSearchResultItem[]> => {
        const resultSet: ICirrusReaderPublicationSearchResultItem[] = [];

        for (const colibrioSearchResultItem of colibrioSearchResultItems) {
            const bookSections = await colibrioSearchResultItem.contentLocation.fetchNavigationItemReferences({
                greedy: false,
                collectionTypes: [NavigationCollectionType.TOC]
            });
            const bookSection = bookSections.getItemsBefore()[0]?.getNavigationItem().getTextContent() || 'unknown';
            const resultItem = {
                bookSection,
                locator: colibrioSearchResultItem.contentLocation.getLocator().toString(),
                nodes: [],
                intersectingNodeText: colibrioSearchResultItem.textBefore + colibrioSearchResultItem.matchingText + colibrioSearchResultItem.textAfter,
                readerDocumentIndexInSpine: colibrioSearchResultItem.readerDocumentIndexInSpine,
                searchTermEndOffsetInIndexedContent: colibrioSearchResultItem.textAfter.length,
                searchTermStartOffsetInIndexedContent: colibrioSearchResultItem.textBefore.length

            };
            resultSet.push(resultItem);
        }

        return resultSet;
    }


    return {
        searchPublication
    }
}